import { api } from '@/shared/api'

export type CatalogAccessState = 'available' | 'partial' | 'locked'

export type CatalogAccess = {
  state: CatalogAccessState
  reasonCode:
    | 'video_grant'
    | 'collection_grant'
    | 'bundle_video_grant'
    | 'bundle_collection_grant'
    | 'partial_video_grants'
    | 'no_access'
    | 'not_released'
}

export type CatalogViewer = {
  subscriptionStatus: string | null
  subscriptionStatusKnown: boolean
  subscriptionActive: boolean
}

export type CatalogFilterOption = {
  id: string
  title: string
  position: number
  queryValue: string
  legacyQueryValue: string | null
}

export type CatalogFilter = {
  id: string
  title: string
  position: number
  queryKey: string
  legacyQueryKey: string | null
  selectedOptionIds: string[]
  options: CatalogFilterOption[]
}

export type CatalogCategory = {
  id: string
  title: string
  urlSlug: string
  position: number
  color: string | null
}

export type CatalogBundle = {
  id: string
  title: string
  createdAt: string
}

export type CatalogListItem = {
  kind: 'collection' | 'video'
  id: string
  title: string
  urlSlug: string
  coverUrl: string | null
  shortDescription: string | null
  totalVideos: number | null
  totalDurationSeconds: number | null
  durationSeconds: number | null
  fileSizeBytes: number | null
  isBookmarked: boolean
  access: CatalogAccess
  openUrl: string | null
}

export type CatalogBrowseCategory = {
  rowKind: 'category' | 'bundle'
  id: string
  title: string
  urlSlug: string | null
  position: number | null
  createdAt: string | null
  items: CatalogListItem[]
  itemsPage: {
    total: number
    limit: number
    nextCursor: string | null
  }
}

export type CatalogBrowseResponse = {
  source: 'categories' | 'bundles'
  rows: CatalogBrowseCategory[]
  page: {
    total: number
    limit: number
    nextCursor: string | null
  }
}

export type CatalogSearchItem = CatalogListItem & {
  categories: string[]
}

export type CatalogSearchResults = {
  total: number
  limit: number
  nextCursor: string | null
  items: CatalogSearchItem[]
}

export type CatalogFavoritesOverview = {
  recentHistory: {
    total: number
    limit: number
    items: CatalogSearchItem[]
  }
}

export type CatalogResponse = {
  contractVersion: string
  viewer: CatalogViewer
  mode: 'browse' | 'search'
  query: {
    search: string | null
    source: 'categories' | 'bundles'
    category: string | null
    bundle: string | null
    selectedFilters: Array<{
      filterId: string
      optionIds: string[]
    }>
    usedLegacyFilterQueryKeys: boolean
    favorites?: boolean
    history?: boolean
    searchLimit?: number
    searchCursor?: string | null
    browseCategoriesLimit?: number
    browseCategoriesCursor?: string | null
    browseCategoryItemsLimit?: number
  }
  categories: CatalogCategory[]
  bundles: CatalogBundle[]
  filters: CatalogFilter[]
  browse: CatalogBrowseResponse | null
  favoritesOverview?: CatalogFavoritesOverview | null
  searchResults: CatalogSearchResults | null
}

export type CatalogCategoryItemsResponse = {
  contractVersion: string
  viewer: CatalogViewer
  category: CatalogCategory
  page: {
    total: number
    limit: number
    nextCursor: string | null
  }
  items: CatalogListItem[]
}

export type CatalogBundleItemsResponse = {
  contractVersion: string
  viewer: CatalogViewer
  bundle: CatalogBundle
  page: {
    total: number
    limit: number
    nextCursor: string | null
  }
  items: CatalogListItem[]
}

export type CatalogResource = {
  id: string
  title: string
  fileName: string
  fileUrl: string | null
  mimeType: string | null
  position: number
}

export type CatalogCollectionVideoItem = {
  id: string
  title: string
  urlSlug: string
  coverUrl: string | null
  shortDescription: string | null
  durationSeconds: number | null
  fileSizeBytes: number | null
  isBookmarked: boolean
  access: CatalogAccess
  openUrl: string | null
}

export type CatalogCollectionDetailResponse = {
  contractVersion: string
  viewer: CatalogViewer
  collection: {
    id: string
    title: string
    urlSlug: string
    descriptionHtml: string | null
    shortDescription: string | null
    coverUrl: string | null
    totalVideos: number | null
    totalDurationSeconds: number | null
    isBookmarked: boolean
    access: CatalogAccess
    openUrl: string | null
    categories: Array<{
      id: string
      title: string
      urlSlug: string
    }>
  }
  videos: CatalogCollectionVideoItem[]
  selectedVideo: {
    id: string
    title: string
    urlSlug: string
    descriptionHtml: string | null
    shortDescription: string | null
    coverUrl: string | null
    access: CatalogAccess
    durationSeconds: number | null
    fileSizeBytes: number | null
    isBookmarked: boolean
    openUrl: string | null
    playback: {
      videoUrl: string | null
      videoBucket: string | null
      videoKey: string | null
      // Заполнено — ролик играет плеер кинескопа; пусто — прежний путь через
      // подписанную ссылку на хранилище.
      kinescopeId: string | null
    } | null
    playbackProgressSeconds: number | null
    playbackCompleted: boolean
    resources: CatalogResource[]
    categories: Array<{
      id: string
      title: string
      urlSlug: string
    }>
  } | null
  resume: {
    videoId: string
    videoUrlSlug: string
    openUrl: string | null
    positionSeconds: number
    durationSeconds: number | null
    completed: boolean
    lastPlayedAt: string | null
  } | null
  resources: CatalogResource[]
  canonicalRedirectUrl: string | null
}

export type CatalogVideoDetailResponse = {
  contractVersion: string
  viewer: CatalogViewer
  video: {
    id: string
    title: string
    urlSlug: string
    descriptionHtml: string | null
    shortDescription: string | null
    coverUrl: string | null
    durationSeconds: number | null
    fileSizeBytes: number | null
    isBookmarked: boolean
    access: CatalogAccess
    openUrl: string | null
    playback: {
      videoUrl: string | null
      videoBucket: string | null
      videoKey: string | null
      // Заполнено — ролик играет плеер кинескопа; пусто — прежний путь через
      // подписанную ссылку на хранилище.
      kinescopeId: string | null
    } | null
    playbackProgressSeconds: number | null
    playbackCompleted: boolean
    playbackLastPlayedAt: string | null
    categories: Array<{
      id: string
      title: string
      urlSlug: string
    }>
  }
  resources: CatalogResource[]
}

const toParamsRecord = (params: URLSearchParams): Record<string, string> => {
  const result: Record<string, string> = {}

  params.forEach((value, key) => {
    const current = result[key]
    if (!current) {
      result[key] = value
      return
    }

    result[key] = `${current},${value}`
  })

  return result
}

export async function fetchCatalog(params: URLSearchParams) {
  return api.get<CatalogResponse>('/api/v1/catalog', {
    params: toParamsRecord(params),
  })
}

export async function fetchCatalogCategoryItems(input: {
  categoryId: string
  cursor?: string | null
  limit?: number
}) {
  const params = new URLSearchParams()

  if (input.cursor) {
    params.set('cursor', input.cursor)
  }

  if (typeof input.limit === 'number') {
    params.set('limit', String(input.limit))
  }

  return api.get<CatalogCategoryItemsResponse>(
    `/api/v1/catalog/browse/categories/${input.categoryId}/items`,
    {
      params: toParamsRecord(params),
    }
  )
}

export async function fetchCatalogBundleItems(input: {
  bundleId: string
  cursor?: string | null
  limit?: number
}) {
  const params = new URLSearchParams()

  if (input.cursor) {
    params.set('cursor', input.cursor)
  }

  if (typeof input.limit === 'number') {
    params.set('limit', String(input.limit))
  }

  return api.get<CatalogBundleItemsResponse>(
    `/api/v1/catalog/browse/bundles/${input.bundleId}/items`,
    {
      params: toParamsRecord(params),
    }
  )
}

export async function fetchCatalogCollectionDetail(input: {
  collectionUrl: string
  selectedVideoSlug?: string | null
}) {
  const params = new URLSearchParams()

  if (input.selectedVideoSlug) {
    params.set('video', input.selectedVideoSlug)
  }

  return api.get<CatalogCollectionDetailResponse>(
    `/api/v1/catalog/collections/${input.collectionUrl}`,
    {
      params: toParamsRecord(params),
    }
  )
}

export async function fetchCatalogVideoDetail(videoUrl: string) {
  return api.get<CatalogVideoDetailResponse>(`/api/v1/catalog/videos/${videoUrl}`)
}

export async function setCatalogFavorite(input: {
  itemKind: 'collection' | 'video'
  itemId: string
  bookmarked: boolean
}) {
  return api.put<{
    itemKind: 'collection' | 'video'
    itemId: string
    isBookmarked: boolean
    favoriteAddedAt: string | null
  }>('/api/v1/catalog/favorites', input)
}

export async function setCatalogPlaybackProgress(input: {
  videoId: string
  collectionId?: string | null
  positionSeconds: number
  durationSeconds?: number | null
  completed?: boolean
}) {
  return api.put<{
    videoId: string
    positionSeconds: number
    durationSeconds: number | null
    completed: boolean
    lastPlayedAt: string
    collectionProgress: {
      collectionId: string
      videoId: string
      positionSeconds: number
      durationSeconds: number | null
      completed: boolean
      lastPlayedAt: string
    } | null
  }>('/api/v1/catalog/progress', input)
}
